import React, { useMemo } from 'react';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineGrid,
  Text,
  Avatar,
  Badge,
  Button,
  ProgressBar,
  Box,
  Divider,
  InlineStack,
  IndexTable,
  useIndexResourceState,
  Tag,
  Tooltip,
} from '@shopify/polaris';
import {
  WalletIcon,
  OrderIcon,
  ChartVerticalFilledIcon,
  CalendarIcon,
  StarFilledIcon,
  ArrowRightIcon,
  GlobeIcon,
  ClockIcon,
  PersonIcon,
  MoneyIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@shopify/polaris-icons';
import { ReactECharts, ReactEChartsProps } from 'components/React-ECharts';
import { Helmet } from 'react-helmet-async';

// Fake Data
const ACCOUNT_DATA = {
  id: 'AFF-2023-8899',
  name: 'Nguyễn Văn An',
  email: 'an.nguyen@example.com',
  avatar: 'https://i.pravatar.cc/150?u=AFF-2023-8899',
  joinDate: '2023-05-15',
  level: 'Gold Partner',
  nextLevel: 'Diamond Partner',
  progressToNextLevel: 75, // percentage
  wallet: {
    balance: 15450000,
    pending: 2300000,
    currency: 'VND',
  },
  diamonds: {
    current: 340,
    nextTier: 500,
    tiers: [
      { threshold: 100, reward: 10, label: '10%' },
      { threshold: 200, reward: 35, label: '35%' },
      { threshold: 500, reward: 50, label: '50%' },
    ],
  },
  bonuses: {
    thisMonth: 1500000,
    lastMonth: 1200000,
  },
  income: {
    thisMonth: 8500000,
    lastMonth: 7200000,
  },
  network: {
    totalMembers: 128,
    activeMembers: 45,
    topPerformers: [
      { name: 'Lê Văn B', sales: 150, revenue: 45000000, bonus: 4500000, rank: 'Silver' },
      { name: 'Trần Thị C', sales: 120, revenue: 32000000, bonus: 3200000, rank: 'Bronze' },
      { name: 'Phạm Văn D', sales: 200, revenue: 65000000, bonus: 6500000, rank: 'Gold' },
      { name: 'Ngô Thị E', sales: 80, revenue: 18000000, bonus: 1800000, rank: 'Iron' },
      { name: 'Hoàng Văn F', sales: 250, revenue: 82000000, bonus: 8200000, rank: 'Diamond' },
    ],
  },
  orders: {
    total: 1250,
    thisMonth: 45,
    pending: 5,
    completed: 38,
    cancelled: 2,
  },
  earnings: {
    total: 156000000,
    thisMonth: 8500000,
    lastMonth: 7200000,
  },
  clicks: {
    total: 15420,
    thisMonth: 3200,
    conversionRate: 2.5, // %
  },
  chartData: {
    months: ['Thg 6', 'Thg 7', 'Thg 8', 'Thg 9', 'Thg 10', 'Thg 11'],
    earnings: [5000000, 6200000, 5800000, 7500000, 7200000, 8500000],
    orders: [25, 32, 28, 40, 38, 45],
    clicks: [1200, 1500, 1400, 2000, 1800, 3200],
  },
  trafficSources: [
    { value: 45, name: 'Facebook' },
    { value: 25, name: 'Google' },
    { value: 15, name: 'Direct' },
    { value: 10, name: 'Tiktok' },
    { value: 5, name: 'Other' },
  ],
  topProducts: [
    {
      id: '1',
      name: 'Áo thun nam cotton cao cấp',
      clicks: 1200,
      orders: 45,
      commission: 1500000,
      conversionRate: 3.75,
    },
    {
      id: '2',
      name: 'Giày sneaker thể thao',
      clicks: 850,
      orders: 20,
      commission: 1200000,
      conversionRate: 2.35,
    },
    {
      id: '3',
      name: 'Đồng hồ thông minh Series 5',
      clicks: 600,
      orders: 15,
      commission: 2500000,
      conversionRate: 2.5,
    },
    {
      id: '4',
      name: 'Tai nghe bluetooth chống ồn',
      clicks: 450,
      orders: 12,
      commission: 900000,
      conversionRate: 2.66,
    },
    {
      id: '5',
      name: 'Balo laptop chống nước',
      clicks: 300,
      orders: 8,
      commission: 400000,
      conversionRate: 2.66,
    },
  ],
  recentActivities: [
    {
      id: '1',
      type: 'order',
      description: 'Đơn hàng mới từ Nguyễn Thị B',
      amount: '+120,000 đ',
      time: '2 phút trước',
    },
    {
      id: '2',
      type: 'payout',
      description: 'Rút tiền về ví Momo',
      amount: '-2,000,000 đ',
      time: '1 giờ trước',
    },
    {
      id: '3',
      type: 'order',
      description: 'Đơn hàng mới từ Trần Văn C',
      amount: '+85,000 đ',
      time: '3 giờ trước',
    },
    {
      id: '4',
      type: 'system',
      description: 'Đạt cấp độ Gold Partner',
      amount: '',
      time: '1 ngày trước',
    },
    {
      id: '5',
      type: 'order',
      description: 'Đơn hàng mới từ Lê Thị D',
      amount: '+210,000 đ',
      time: '1 ngày trước',
    },
  ],
};

export default function AgencyDashboard() {
  // Helpers
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
  };

  const calculateTenure = (dateString: string) => {
    const start = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) return `${diffDays} ngày`;
    const months = Math.floor(diffDays / 30);
    if (months < 12) return `${months} tháng`;
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    return remainingMonths > 0 ? `${years} năm ${remainingMonths} tháng` : `${years} năm`;
  };

  const calculateGrowth = (current: number, previous: number) => {
    if (previous === 0) return 100;
    return (((current - previous) / previous) * 100).toFixed(1);
  };

  // Chart Options
  const earningsChartOption: ReactEChartsProps['option'] = useMemo(
    () => ({
      title: {
        text: 'Hiệu quả hoạt động (6 tháng)',
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 'normal' },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
      },
      legend: {
        data: ['Thu nhập', 'Đơn hàng', 'Clicks'],
        bottom: 0,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        containLabel: true,
      },
      xAxis: [
        {
          type: 'category',
          data: ACCOUNT_DATA.chartData.months,
          axisPointer: { type: 'shadow' },
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: 'Thu nhập',
          min: 0,
          axisLabel: { formatter: '{value} đ' },
        },
        {
          type: 'value',
          name: 'Số lượng',
          min: 0,
          position: 'right',
          axisLabel: { formatter: '{value}' },
        },
      ],
      series: [
        {
          name: 'Thu nhập',
          type: 'bar',
          tooltip: { valueFormatter: (value: any) => formatCurrency(value as number) },
          data: ACCOUNT_DATA.chartData.earnings,
          itemStyle: { color: '#008060' }, // Shopify Green
        },
        {
          name: 'Clicks',
          type: 'line',
          yAxisIndex: 1,
          tooltip: { valueFormatter: (value: any) => value + ' clicks' },
          data: ACCOUNT_DATA.chartData.clicks,
          itemStyle: { color: '#5c5f62' }, // Shopify Gray
        },
        {
          name: 'Đơn hàng',
          type: 'line',
          yAxisIndex: 1,
          tooltip: { valueFormatter: (value: any) => value + ' đơn' },
          data: ACCOUNT_DATA.chartData.orders,
          itemStyle: { color: '#005bd3' }, // Shopify Blue
        },
      ],
    }),
    []
  );

  const trafficSourceChartOption: ReactEChartsProps['option'] = useMemo(
    () => ({
      title: {
        text: 'Nguồn truy cập',
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 'normal' },
      },
      tooltip: {
        trigger: 'item',
      },
      legend: {
        bottom: 0,
        left: 'center',
      },
      series: [
        {
          name: 'Nguồn',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: '16',
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: ACCOUNT_DATA.trafficSources,
        },
      ],
    }),
    []
  );

  const networkBubbleChartOption: ReactEChartsProps['option'] = useMemo(
    () => ({
      title: {
        text: 'Hiệu quả hệ thống (Bubble Cloud)',
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 'normal' },
      },
      tooltip: {
        formatter: function (param: any) {
          if (param.dataType === 'node') {
            return (
              param.data.name +
              '<br/>' +
              'Doanh thu: ' +
              formatCurrency(param.data.value) +
              '<br/>' +
              'Rank: ' +
              param.data.rank +
              '<br/>' +
              'Thưởng: ' +
              formatCurrency(param.data.bonus)
            );
          }
          return '';
        },
      },
      series: [
        {
          type: 'graph',
          layout: 'force',
          force: {
            repulsion: 60,
            gravity: 0.1,
            edgeLength: 5,
            layoutAnimation: true,
          },
          roam: true,
          label: {
            show: true,
            position: 'inside',
            formatter: '{b}',
            fontSize: 10,
            color: '#000',
          },
          data: ACCOUNT_DATA.network.topPerformers.map((p) => ({
            name: p.name,
            value: p.revenue,
            bonus: p.bonus,
            rank: p.rank,
            symbolSize: Math.sqrt(p.revenue) / 150,
            draggable: true,
            itemStyle: {
              color: (function () {
                const colors: any = {
                  Silver: '#C0C0C0',
                  Bronze: '#CD7F32',
                  Gold: '#FFD700',
                  Iron: '#a19d94',
                  Diamond: '#b9f2ff',
                };
                return colors[p.rank] || '#5c5f62';
              })(),
              borderColor: '#fff',
              borderWidth: 1,
              shadowBlur: 5,
              shadowColor: 'rgba(0, 0, 0, 0.3)',
            },
          })),
        },
      ],
    }),
    []
  );

  const resourceName = {
    singular: 'product',
    plural: 'products',
  };

  const { selectedResources, allResourcesSelected, handleSelectionChange } = useIndexResourceState(ACCOUNT_DATA.topProducts);

  const rowMarkup = ACCOUNT_DATA.topProducts.map(({ id, name, clicks, orders, commission, conversionRate }, index) => (
    <IndexTable.Row id={id} key={id} selected={selectedResources.includes(id)} position={index}>
      <IndexTable.Cell>
        <Text variant="bodyMd" fontWeight="bold" as="span">
          {name}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{clicks}</IndexTable.Cell>
      <IndexTable.Cell>{orders}</IndexTable.Cell>
      <IndexTable.Cell>{conversionRate}%</IndexTable.Cell>
      <IndexTable.Cell>{formatCurrency(commission)}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  const diamondProgress = (ACCOUNT_DATA.diamonds.current / ACCOUNT_DATA.diamonds.nextTier) * 100;

  return (
    <Page title="Dashboard Affiliate" fullWidth>
      <Helmet>
        <title>Dashboard Affiliate - Tổng quan hiệu quả</title>
      </Helmet>

      <Layout>
        {/* Top Stats Cards */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 5 }} gap="400">
            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text variant="headingSm" as="h3">
                    Số dư ví
                  </Text>
                  <WalletIcon width={20} color="#5c5f62" />
                </InlineStack>
                <Text variant="headingXl" as="p">
                  {formatCurrency(ACCOUNT_DATA.wallet.balance)}
                </Text>
                <Text variant="bodySm" as="p" tone="subdued">
                  Chờ duyệt: {formatCurrency(ACCOUNT_DATA.wallet.pending)}
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text variant="headingSm" as="h3">
                    Kim cương
                  </Text>
                  <MoneyIcon width={20} color="#00a0ac" />
                </InlineStack>
                <Text variant="headingXl" as="p">
                  {ACCOUNT_DATA.diamonds.current} 💎
                </Text>
                <Text variant="bodySm" as="p" tone="subdued">
                  Còn {ACCOUNT_DATA.diamonds.nextTier - ACCOUNT_DATA.diamonds.current} để đạt mốc tiếp theo
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text variant="headingSm" as="h3">
                    Thành viên hệ thống
                  </Text>
                  <PersonIcon width={20} color="#5c5f62" />
                </InlineStack>
                <Text variant="headingXl" as="p">
                  {ACCOUNT_DATA.network.totalMembers}
                </Text>
                <Text variant="bodySm" as="p" tone="subdued">
                  {ACCOUNT_DATA.network.activeMembers} đang hoạt động
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text variant="headingSm" as="h3">
                    Thưởng tháng này
                  </Text>
                  <StarFilledIcon width={20} color="#e49400" />
                </InlineStack>
                <Text variant="headingXl" as="p">
                  {formatCurrency(ACCOUNT_DATA.bonuses.thisMonth)}
                </Text>
                <InlineStack gap="100" align="start">
                  {ACCOUNT_DATA.bonuses.thisMonth >= ACCOUNT_DATA.bonuses.lastMonth ? (
                    <ArrowUpIcon width={20} color="success" />
                  ) : (
                    <ArrowDownIcon width={20} color="critical" />
                  )}
                  <Text variant="bodySm" as="span" tone={ACCOUNT_DATA.bonuses.thisMonth >= ACCOUNT_DATA.bonuses.lastMonth ? 'success' : 'critical'}>
                    {calculateGrowth(ACCOUNT_DATA.bonuses.thisMonth, ACCOUNT_DATA.bonuses.lastMonth)}%
                  </Text>
                  <Text variant="bodySm" as="span" tone="subdued">
                    vs tháng trước ({formatCurrency(ACCOUNT_DATA.bonuses.lastMonth)})
                  </Text>
                </InlineStack>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text variant="headingSm" as="h3">
                    Thu nhập tháng này
                  </Text>
                  <ChartVerticalFilledIcon width={20} color="#5c5f62" />
                </InlineStack>
                <Text variant="headingXl" as="p">
                  {formatCurrency(ACCOUNT_DATA.income.thisMonth)}
                </Text>
                <InlineStack gap="100" align="start">
                  {ACCOUNT_DATA.income.thisMonth >= ACCOUNT_DATA.income.lastMonth ? (
                    <ArrowUpIcon width={20} color="success" />
                  ) : (
                    <ArrowDownIcon width={20} color="critical" />
                  )}
                  <Text variant="bodySm" as="span" tone={ACCOUNT_DATA.income.thisMonth >= ACCOUNT_DATA.income.lastMonth ? 'success' : 'critical'}>
                    {calculateGrowth(ACCOUNT_DATA.income.thisMonth, ACCOUNT_DATA.income.lastMonth)}%
                  </Text>
                  <Text variant="bodySm" as="span" tone="subdued">
                    vs tháng trước ({formatCurrency(ACCOUNT_DATA.income.lastMonth)})
                  </Text>
                </InlineStack>
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* Diamond Reward Progress */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <InlineStack gap="200">
                  <StarFilledIcon width={24} color="#00a0ac" />
                  <Text variant="headingMd" as="h3">
                    Tiến độ thưởng Kim Cương
                  </Text>
                </InlineStack>
                <Text variant="bodyMd" as="span">
                  Bạn đang ở mức thưởng: <strong>35%</strong>
                </Text>
              </InlineStack>

              <Box position="relative" paddingBlockStart="400" paddingBlockEnd="800">
                {/* Progress Bar Background */}
                <div style={{ height: '8px', backgroundColor: '#f1f2f3', borderRadius: '4px', position: 'relative', width: '100%' }}>
                  {/* Active Progress */}
                  <div
                    style={{
                      height: '100%',
                      backgroundColor: '#00a0ac',
                      borderRadius: '4px',
                      width: `${Math.min(diamondProgress, 100)}%`,
                      transition: 'width 0.5s ease-in-out',
                    }}
                  />

                  {/* Milestones */}
                  {ACCOUNT_DATA.diamonds.tiers.map((tier, index) => {
                    const position = (tier.threshold / ACCOUNT_DATA.diamonds.nextTier) * 100;
                    const isReached = ACCOUNT_DATA.diamonds.current >= tier.threshold;
                    return (
                      <div
                        key={index}
                        style={{
                          position: 'absolute',
                          left: `${position}%`,
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: isReached ? '#00a0ac' : '#fff',
                            border: `2px solid ${isReached ? '#00a0ac' : '#8c9196'}`,
                            zIndex: 2,
                          }}
                        />
                        <div style={{ marginTop: '8px', textAlign: 'center', minWidth: '60px' }}>
                          <Text variant="bodyXs" as="p" fontWeight="bold">
                            {tier.threshold} 💎
                          </Text>
                          <Text variant="bodyXs" as="p" tone="subdued">
                            {tier.label}
                          </Text>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Box>

              <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                <InlineStack gap="200" align="start">
                  <StarFilledIcon width={20} color="warning" />
                  <Text variant="bodySm" as="p">
                    Chỉ còn <strong>{ACCOUNT_DATA.diamonds.nextTier - ACCOUNT_DATA.diamonds.current} kim cương</strong> nữa để đạt mốc thưởng{' '}
                    <strong>50%</strong>! Hãy cố gắng lên!
                  </Text>
                </InlineStack>
              </Box>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Network & Traffic Charts */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, md: '1fr 1fr' }} gap="400">
            <Card>
              <ReactECharts option={networkBubbleChartOption} style={{ height: '400px', width: '100%' }} />
            </Card>
            <Card>
              <ReactECharts option={trafficSourceChartOption} style={{ height: '400px', width: '100%' }} />
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* Earnings Chart */}
        <Layout.Section>
          <Card>
            <ReactECharts option={earningsChartOption} style={{ height: '400px', width: '100%' }} />
          </Card>
        </Layout.Section>

        {/* Top Products Table */}
        <Layout.Section>
          <Card padding="0">
            <Box padding="400">
              <Text variant="headingMd" as="h3">
                Top sản phẩm hiệu quả
              </Text>
            </Box>
            <IndexTable
              resourceName={resourceName}
              itemCount={ACCOUNT_DATA.topProducts.length}
              selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              headings={[{ title: 'Sản phẩm' }, { title: 'Clicks' }, { title: 'Đơn hàng' }, { title: 'Chuyển đổi' }, { title: 'Hoa hồng' }]}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>

        {/* Recent Activity & Profile Summary */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, md: '1fr 1fr' }} gap="400">
            {/* Recent Activity */}
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text variant="headingMd" as="h3">
                    Hoạt động gần đây
                  </Text>
                  <Button variant="plain">Xem tất cả</Button>
                </InlineStack>
                <BlockStack gap="400">
                  {ACCOUNT_DATA.recentActivities.map((activity) => (
                    <InlineStack key={activity.id} align="space-between" blockAlign="center">
                      <InlineStack gap="300">
                        <Box padding="200" background={activity.type === 'payout' ? 'bg-surface-critical' : 'bg-surface-success'} borderRadius="200">
                          {activity.type === 'payout' ? (
                            <WalletIcon width={20} />
                          ) : activity.type === 'system' ? (
                            <StarFilledIcon width={20} />
                          ) : (
                            <OrderIcon width={20} />
                          )}
                        </Box>
                        <BlockStack>
                          <Text variant="bodyMd" as="span" fontWeight="bold">
                            {activity.description}
                          </Text>
                          <Text variant="bodySm" as="span" tone="subdued">
                            {activity.time}
                          </Text>
                        </BlockStack>
                      </InlineStack>
                      <Text variant="bodyMd" as="span" fontWeight="bold" tone={activity.type === 'payout' ? 'critical' : 'success'}>
                        {activity.amount}
                      </Text>
                    </InlineStack>
                  ))}
                </BlockStack>
              </BlockStack>
            </Card>

            {/* Profile & Progress */}
            <Card>
              <BlockStack gap="500">
                <InlineStack align="space-between">
                  <Text variant="headingMd" as="h3">
                    Hồ sơ đối tác
                  </Text>
                  <Button variant="plain" icon={ArrowRightIcon}>
                    Chi tiết
                  </Button>
                </InlineStack>

                <InlineGrid columns="auto 1fr" gap="400" alignItems="center">
                  <Avatar source={ACCOUNT_DATA.avatar} size="lg" name={ACCOUNT_DATA.name} />
                  <BlockStack gap="100">
                    <Text variant="headingMd" as="h2">
                      {ACCOUNT_DATA.name}
                    </Text>
                    <InlineStack gap="200">
                      <Badge tone="success">{ACCOUNT_DATA.level}</Badge>
                      <InlineStack gap="100" align="center">
                        <ClockIcon width={16} color="subdued" />
                        <Text variant="bodySm" as="span" tone="subdued">
                          {calculateTenure(ACCOUNT_DATA.joinDate)}
                        </Text>
                      </InlineStack>
                    </InlineStack>
                  </BlockStack>
                </InlineGrid>

                <Divider />

                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text variant="bodySm" as="span">
                      Tiến độ lên <strong>{ACCOUNT_DATA.nextLevel}</strong>
                    </Text>
                    <Text variant="bodySm" as="span">
                      {ACCOUNT_DATA.progressToNextLevel}%
                    </Text>
                  </InlineStack>
                  <ProgressBar progress={ACCOUNT_DATA.progressToNextLevel} tone="primary" size="small" />
                  <Box padding="300" background="bg-surface-secondary" borderRadius="200">
                    <InlineStack gap="200" align="start">
                      <StarFilledIcon width={20} color="warning" />
                      <Text variant="bodySm" as="p">
                        Bạn chỉ còn thiếu <strong>5,000,000 đ</strong> doanh số để đạt cấp độ Diamond và nhận thêm 2% hoa hồng!
                      </Text>
                    </InlineStack>
                  </Box>
                </BlockStack>
              </BlockStack>
            </Card>
          </InlineGrid>
        </Layout.Section>
      </Layout>

      {/** Xuống dòng để hở cái chân page ra @jamviet  */}
      <br />
      <br />
      <br />
    </Page>
  );
}

